/**
 * Waterfall timings for the initial load tree.
 *
 * Import `perf` from any module or Vue component. Spans show up in DevTools
 * as User Timing marks named `aeppic:<step>`. Call `perf.report()` from the
 * console, or open the app with `?perf=1` / `?boottrace=1`, or set
 * localStorage `ae-perf` to `1`.
 *
 * `perf.begin('ui')` keeps a root span on the stack so later loads nest under
 * it. The root closes, and a table is printed, only after no span is open for
 * a short quiet window. That is the time until the loading tree is idle, not
 * the time until the boot shell returns.
 */

export interface PerfRow {
  name: string
  at: number
  wall: number
  network: number
  slowest: string
  bound: 'yes' | 'no'
  detail: string
}

interface PerfSpan {
  id: number
  name: string
  start: number
  end?: number
  detail: string
  parentId?: number
  network: number
  slowest: string
}

export interface PerfHandle {
  end: (detail?: string) => void
}

export interface PerfSessionOptions {
  quietMs?: number
  capMs?: number
}

const QUERY_STAGES = [
  'query-total', 'worker-ack', 'worker-roundtrip', 'local-route', 'local-plan',
  'sqlite', 'local-decode', 'remote-queue', 'remote-http', 'remote-query',
] as const
export type QueryPerfStage = typeof QUERY_STAGES[number]
export type QueryPerfStatus = 'ok' | 'error' | 'timeout'
export interface QueryPerfRow {
  stage: QueryPerfStage
  count: number
  samples: number
  errors: number
  timeouts: number
  meanMs: number
  p50Ms: number
  p95Ms: number
  p99Ms: number
  maxMs: number
}
interface QuerySamples {
  count: number
  errors: number
  timeouts: number
  next: number
  values: number[]
}

const MAX_SPANS = 8000
const DEFAULT_QUIET_MS = 800
const DEFAULT_CAP_MS = 20000

function now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now()
  return Date.now()
}

function reportingEnabled(): boolean {
  try {
    if (typeof location !== 'undefined') {
      const params = new URLSearchParams(location.search)
      if (params.has('perf') || params.has('boottrace')) return true
    }
    if (typeof localStorage !== 'undefined' && localStorage.getItem('ae-perf') === '1') return true
  } catch {
    return false
  }
  return false
}

function resourcesDuring(start: number, end: number): { total: number, slowestMs: number, slowestName: string } {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return { total: 0, slowestMs: 0, slowestName: '' }
  }
  let total = 0
  let slowestMs = 0
  let slowestName = ''
  for (const entry of performance.getEntriesByType('resource')) {
    const resource = entry as PerformanceResourceTiming
    if (resource.startTime < start || resource.startTime >= end) continue
    const duration = resource.duration || 0
    total += duration
    if (duration > slowestMs) {
      slowestMs = duration
      slowestName = resource.name.replace(typeof location !== 'undefined' ? location.origin : '', '')
    }
  }
  return { total, slowestMs, slowestName }
}

class Perf {
  private seq = 0
  private spans: PerfSpan[] = []
  private stack: PerfSpan[] = []
  private session: PerfSpan | null = null
  private quietTimer: ReturnType<typeof setTimeout> | null = null
  private capTimer: ReturnType<typeof setTimeout> | null = null
  private quietMs = DEFAULT_QUIET_MS
  private idleCallbacks: Array<() => void> = []

  private querySampleLimit = 1024
  private querySamples = new Map<QueryPerfStage, QuerySamples>()

  /** Configure the rolling window (samples per stage), clearing existing query metrics. */
  configureQueries({ sampleLimit = 1024 }: { sampleLimit?: number } = {}): void {
    if (!Number.isSafeInteger(sampleLimit) || sampleLimit < 1) throw new RangeError('sampleLimit must be a positive integer')
    this.querySampleLimit = sampleLimit
    this.resetQueries()
  }

  recordQuery(stage: QueryPerfStage, durationMs: number, status: QueryPerfStatus = 'ok'): void {
    if (!QUERY_STAGES.includes(stage) || !Number.isFinite(durationMs) || durationMs < 0) return
    let samples = this.querySamples.get(stage)
    if (!samples) {
      samples = { count: 0, errors: 0, timeouts: 0, next: 0, values: [] }
      this.querySamples.set(stage, samples)
    }
    samples.count++
    if (status !== 'ok') samples.errors++
    if (status === 'timeout') samples.timeouts++
    samples.values[samples.next] = durationMs
    samples.next = (samples.next + 1) % this.querySampleLimit
  }

  /** Timers use one monotonic clock and record once even if completed twice. */
  startQuery(stage: QueryPerfStage): (status?: QueryPerfStatus) => void {
    const start = now()
    let ended = false
    return (status = 'ok') => {
      if (ended) return
      ended = true
      this.recordQuery(stage, now() - start, status)
    }
  }

  async trackQuery<T>(stage: QueryPerfStage, fn: () => Promise<T> | T): Promise<T> {
    const end = this.startQuery(stage)
    try {
      const result = await fn()
      end()
      return result
    } catch (error) {
      end(error?.code === 'SERVER_QUERY_TIMEOUT' ? 'timeout' : 'error')
      throw error
    }
  }

  /** Nearest-rank percentiles over the latest samples; counters cover time since reset. */
  querySnapshot(): QueryPerfRow[] {
    return [...this.querySamples].map(([stage, samples]) => {
      const sorted = [...samples.values].sort((a, b) => a - b)
      const percentile = (fraction: number) => sorted[Math.ceil(fraction * sorted.length) - 1]
      return {
        stage, count: samples.count, samples: sorted.length, errors: samples.errors, timeouts: samples.timeouts,
        meanMs: sorted.reduce((sum, value) => sum + value, 0) / sorted.length,
        p50Ms: percentile(0.5), p95Ms: percentile(0.95), p99Ms: percentile(0.99), maxMs: sorted[sorted.length - 1],
      }
    })
  }

  queryReport(): QueryPerfRow[] {
    const rows = this.querySnapshot()
    console.table(rows)
    return rows
  }

  resetQueries(): void { this.querySamples.clear() }

  /**
   * Open a root span that stays on the stack until the load tree is quiet.
   * Later `track` calls nest under it. One report is printed when the tree
   * has had no open span for `quietMs`, or at `capMs` if something never ends.
   */
  /** Run once, when the load-tree session closes. Runs immediately if none is open. */
  whenIdle(callback: () => void): void {
    if (!this.session || this.session.end != null) {
      callback()
      return
    }
    this.idleCallbacks.push(callback)
  }

  begin(name = 'ui', options: PerfSessionOptions = {}): void {
    if (this.session) return
    this.quietMs = options.quietMs ?? DEFAULT_QUIET_MS
    const capMs = options.capMs ?? DEFAULT_CAP_MS
    this.start(name)
    this.session = this.stack[this.stack.length - 1] || null
    this.capTimer = setTimeout(() => this.finishSession('ui stable (capped)'), capMs)
  }

  start(name: string, detail = ''): PerfHandle {
    this.cancelQuiet()
    const span: PerfSpan = {
      id: ++this.seq,
      name,
      start: now(),
      detail,
      parentId: this.stack.length ? this.stack[this.stack.length - 1].id : undefined,
      network: 0,
      slowest: '',
    }
    this.spans.push(span)
    this.stack.push(span)
    if (this.spans.length > MAX_SPANS) this.spans.splice(0, this.spans.length - MAX_SPANS)
    try { performance.mark?.(`aeppic:${name}:start`) } catch { /* ignore */ }
    return { end: (nextDetail?: string) => this.end(span.id, nextDetail) }
  }

  end(id: number, detail?: string): void {
    const span = this.spans.find(candidate => candidate.id === id)
    if (!span || span.end != null) return
    span.end = now()
    if (detail) span.detail = detail
    this.stack = this.stack.filter(candidate => candidate.id !== id)
    const network = resourcesDuring(span.start, span.end)
    span.network = Math.round(network.total)
    span.slowest = network.slowestMs ? `${Math.round(network.slowestMs)}ms ${network.slowestName}` : ''
    try {
      performance.mark?.(`aeppic:${span.name}:end`)
      performance.measure?.(`aeppic:${span.name}`, { start: span.start, end: span.end })
    } catch { /* ignore */ }
    this.armQuiet()
  }

  async track<T>(name: string, fn: () => Promise<T> | T, detail = ''): Promise<T> {
    const handle = this.start(name, detail)
    try {
      return await fn()
    } finally {
      handle.end()
    }
  }

  mark(name: string, detail = ''): void {
    const handle = this.start(name, detail)
    handle.end()
  }

  snapshot(): PerfRow[] {
    const depthOf = (span: PerfSpan): number => {
      let depth = 0
      let parentId = span.parentId
      while (parentId != null) {
        depth += 1
        parentId = this.spans.find(candidate => candidate.id === parentId)?.parentId
      }
      return depth
    }
    return this.spans.filter(span => span.end != null).map(span => {
      const wall = Math.round((span.end as number) - span.start)
      return {
        name: `${'  '.repeat(depthOf(span))}${span.name}`,
        at: Math.round(span.start),
        wall,
        network: span.network,
        slowest: span.slowest,
        bound: span.network >= 50 && span.network >= wall * 0.5 ? 'yes' as const : 'no' as const,
        detail: span.detail,
      }
    })
  }

  report(label = 'perf'): PerfRow[] {
    const rows = this.snapshot()
    if (typeof console !== 'undefined' && console.group) {
      console.group(`[perf] ${label} ${rows.length} steps`)
      console.table(rows)
      console.groupEnd()
    }
    return rows
  }

  reset(): void {
    this.resetQueries()
    this.cancelQuiet()
    if (this.capTimer) clearTimeout(this.capTimer)
    this.capTimer = null
    this.session = null
    this.idleCallbacks = []
    this.spans = []
    this.stack = []
  }

  private armQuiet(): void {
    if (!this.session || this.session.end != null || this.quietTimer) return
    if (this.stack.length !== 1 || this.stack[0] !== this.session) return
    this.quietTimer = setTimeout(() => this.finishSession('ui stable'), this.quietMs)
  }

  private cancelQuiet(): void {
    if (!this.quietTimer) return
    clearTimeout(this.quietTimer)
    this.quietTimer = null
  }

  private finishSession(label: string): void {
    const session = this.session
    if (!session || session.end != null) return
    this.session = null
    this.cancelQuiet()
    if (this.capTimer) clearTimeout(this.capTimer)
    this.capTimer = null
    const stillOpen = this.stack.filter(span => span.id !== session.id && span.end == null)
    for (const span of stillOpen.reverse()) {
      const detail = span.detail ? `${span.detail} (still open)` : 'still open'
      this.end(span.id, detail)
    }
    this.end(session.id)
    if (reportingEnabled()) this.report(label)
    const callbacks = this.idleCallbacks
    this.idleCallbacks = []
    for (const callback of callbacks) callback()
  }
}

export const perf = new Perf()

if (typeof globalThis !== 'undefined') {
  (globalThis as { __aeppicPerf?: Perf }).__aeppicPerf = perf
}
